import { Alert, Button, Card, Form, Input, Space } from 'antd'
import {
    ArrowRightOutlined,
    BarChartOutlined,
    LockOutlined,
    MailOutlined,
    SafetyCertificateOutlined,
    TeamOutlined,
} from '@ant-design/icons'
import useSimpleCaptcha from 'hooks/useSimpleCaptcha'
import jwtDecode from 'jwt-decode'
import moment from 'moment'
import React, { useEffect, useRef, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { RouteConstant } from 'routes/routesConstant'
import { resolvePostLoginPath } from 'services/auth/postLoginRoute'
import { hasPendingOnboarding, hasRecoverableAdminOnboarding, isMockSubscriptionOnboardingEnabled, savePricingIntentFromRedirect } from 'services/auth/onboardingStatus'
import { saveOidcPostLoginRedirect } from 'services/auth/oidcRedirect'
import useAuthRepo from 'services/repo/useAuthRepo'
import { getTenantMemberships, provisionTemporaryWorkspace, saveSelectedTenant } from 'services/tenant/tenantMembershipService'
import Constant from 'utils/Constants'
import DevLog from 'utils/DevLog'
import { dateGreaterThan } from 'utils/Helper'
import LocalStorageUtils from 'utils/LocalStorage'
import { successToast } from 'utils/Toast'
import BrandLogo from 'assets/img/workplansiq-logo.svg'

// import useEncriptRSAHook from 'hooks/useEncriptRSAHook'

const propsItem = {
    colon: false,
    labelCol: { sm: 12 },
    validateTrigger: false,
    className: "form-item-login"
}

const styleFormItem = {
    marginBottom: "0"
}

const getSafeRedirect = (search) => {
    const redirectTo = new URLSearchParams(search).get('redirectTo')
    return redirectTo?.startsWith('/') && !redirectTo.startsWith('//')
        ? redirectTo
        : null
}

const getPostLoginPath = async (search) => {
    const redirectTo = getSafeRedirect(search)
    let memberships = await getTenantMemberships()
    if (memberships.length === 1) saveSelectedTenant(memberships[0])
    const signedInEmail = sessionStorage.getItem('workplansiq.signedInEmail')
    savePricingIntentFromRedirect(redirectTo, signedInEmail)
    const shouldResumeSubscription = hasPendingOnboarding(signedInEmail)
        || hasRecoverableAdminOnboarding(signedInEmail)
    if (!memberships.length && (shouldResumeSubscription || isMockSubscriptionOnboardingEnabled())) {
        memberships = [provisionTemporaryWorkspace({ ownerEmail: signedInEmail })]
    }
    return resolvePostLoginPath({ redirectTo, memberships, hasPendingOnboarding: shouldResumeSubscription })
}

export default function Login() {
    const { login, startGoogleOidc } = useAuthRepo(false);
    const {
        captchaUI,
        isValid: isValidCaptcha,
        handleInputCaptchaChange,
        generateCaptcha
    } = useSimpleCaptcha();
    // const { getEncriptedText } = useEncriptRSAHook()

    const [username, setUsername] = useState("")
    const [password, setPassword] = useState("")
    const [errorMessage, setErrorMessage] = useState("");
    const [, setCaptcha] = useState("")
    const [tryLoginCount, setTryLoginCount] = useState(Number(LocalStorageUtils.tryLoginCount.get()))
    const navigate = useNavigate()
    let location = useLocation();
    const [isLoadingLogin, setIsLoadingLogin] = useState(false)
    const [isLoadingGoogle, setIsLoadingGoogle] = useState(false)
    const [isAlreadyHaveJWT, setIsAlreadyHaveJWT] = useState(false)

    const checkHasJWT = useRef(true)

    const onFinish = async () => {
        setIsLoadingLogin(true)
        checkHasJWT.current = false

        const res = await login(username, password)
        if (LocalStorageUtils.jwtToken.get()) {
            sessionStorage.setItem('workplansiq.signedInEmail', username.trim())

            successToast('Login success! Redirecting...')
            setTimeout(async () => {
                const destination = await getPostLoginPath(location.search)
                navigate(destination, { replace: true })
            }, 1500)
        } else {
            const sumCurrentCount = Number(LocalStorageUtils.tryLoginCount.get()) + 1
            LocalStorageUtils.tryLoginCount.set(sumCurrentCount)
            setTryLoginCount(sumCurrentCount)

            if (sumCurrentCount >= 3) {
                //window.location.reload()
            }

             setErrorMessage(res?.detail || "Login failed. Please try again.");
            setIsLoadingLogin(false)
            checkHasJWT.current = true
        }
    }


    const onFinishFailed = (errorInfo) => {
        DevLog('Failed:', errorInfo);
    };

    const handleGoogleLogin = async () => {
        setErrorMessage("")
        setIsLoadingGoogle(true)

        const redirectTo = getSafeRedirect(location.search)
        saveOidcPostLoginRedirect(redirectTo)

        const response = await startGoogleOidc()
        const authorizationUrl = response?.authorizationUrl || response?.data?.authorizationUrl

        if (!authorizationUrl) {
            setErrorMessage(response?.detail || "Unable to start Google sign-in. Please try again.")
            setIsLoadingGoogle(false)
            return
        }

        window.location.assign(authorizationUrl)
    }

    useEffect(() => {
        if (LocalStorageUtils.jwtToken.get() !== '') {
            tryRelog()
        }
    }, [])

    useEffect(() => {
        const intervalId = setInterval(() => {
            const currentToken = LocalStorageUtils.jwtToken.get();
            if (currentToken !== '' && checkHasJWT.current) {
                if (!LocalStorageUtils.checkTokenExpired()) {
                    setIsAlreadyHaveJWT(true);
                }
            }
        }, 1000);

        return () => {
            clearInterval(intervalId);
        };
    }, [])

    const tryRelog = async () => {
        let token = LocalStorageUtils.jwtToken.get()
        let decoded = jwtDecode(token);
        let exp = moment.unix(decoded?.exp)
        let checkDiffTime = dateGreaterThan(moment(), exp)
        if (!checkDiffTime) {
            const destination = await getPostLoginPath(location.search)
            navigate(destination, { replace: true })
        }
    }

    return (
        <div className='login-page'>
            <div className='wrap-login'>
                <div className="login-shell">
                    <section className="login-brand-panel">
                        <img src={BrandLogo} alt="WorkPlansIQ" className="login-brand-logo" />
                        <div className="login-brand-copy">
                            <span className="login-eyebrow">PRECISION WORKFORCE ANALYTICS</span>
                            <h1>Make workforce decisions with confidence.</h1>
                            <p>Turn workload and capacity data into clear workforce decisions.</p>
                        </div>
                        <div className="login-benefits">
                            <div><BarChartOutlined /><span><strong>See workload and capacity clearly</strong>Spot underused capacity and demand gaps.</span></div>
                            <div><TeamOutlined /><span><strong>Build evidence-based plans</strong>Turn data into decisions leaders can act on.</span></div>
                            <div><SafetyCertificateOutlined /><span><strong>Plan with secure, trusted data</strong>Keep workforce decisions governed and defensible.</span></div>
                        </div>
                    </section>

                    <section className="login-form-panel">
                        {isAlreadyHaveJWT && <div className="alert-already-login">
                            <Alert
                                message={'You signed in with another tab or window. Reload to refresh your session!'}
                                type="warning"
                                action={
                                    <Space>
                                        <Button type="primary" size="small" onClick={() => window.location.reload()}>
                                            Reload
                                        </Button>
                                    </Space>
                                }
                            />
                        </div>}

                        <Card className={`card${errorMessage ? ' login-has-error' : ''}`}>
                            <a className="login-back-home" href={Constant.commercialWebURL}>← Back to home</a>
                            <span className="auth-container-login-eyebrow">WELCOME BACK</span>
                            <h2 className="auth-container-login-title">Sign in to WorkPlansIQ</h2>
                            <p className="auth-container-login-subtitle">
                                Enter your work account details to continue.
                            </p>

                            {errorMessage ? (
                                <Alert
                                    className="login-error"
                                    message={errorMessage}
                                    type="error"
                                    showIcon
                                />
                            ) : null}

                            <Form
                                layout="vertical"
                                name="login"
                                initialValues={{ remember: true }}
                                onFinish={onFinish}
                                onFinishFailed={onFinishFailed}
                                autoComplete="off"
                            >
                                <Button
                                    block
                                    className="login-google"
                                    size="large"
                                    loading={isLoadingGoogle}
                                    disabled={isAlreadyHaveJWT || isLoadingLogin}
                                    onClick={handleGoogleLogin}
                                >
                                    {!isLoadingGoogle ? (
                                        <svg aria-hidden="true" viewBox="0 0 24 24" className="google-mark">
                                            <path fill="#4285F4" d="M21.6 12.23c0-.71-.06-1.4-.18-2.07H12v3.92h5.38a4.6 4.6 0 0 1-2 3.02v2.54h3.24c1.9-1.75 2.98-4.33 2.98-7.41Z" />
                                            <path fill="#34A853" d="M12 22c2.7 0 4.98-.9 6.63-2.36l-3.24-2.54c-.9.6-2.05.96-3.39.96-2.61 0-4.82-1.76-5.61-4.13H3.04v2.62A10 10 0 0 0 12 22Z" />
                                            <path fill="#FBBC05" d="M6.39 13.93A6.02 6.02 0 0 1 6.08 12c0-.67.11-1.32.31-1.93V7.45H3.04A10 10 0 0 0 2 12c0 1.61.39 3.14 1.04 4.55l3.35-2.62Z" />
                                            <path fill="#EA4335" d="M12 5.94c1.47 0 2.79.5 3.83 1.5l2.87-2.87A9.63 9.63 0 0 0 12 2a10 10 0 0 0-8.96 5.45l3.35 2.62C7.18 7.7 9.39 5.94 12 5.94Z" />
                                        </svg>
                                    ) : null}
                                    Continue with Google
                                </Button>

                                <div className="login-divider"><span>or</span></div>

                                <Form.Item
                                    {...propsItem}
                                    label="Work email"
                                    name="username"
                                    style={styleFormItem}
                                    rules={[{ required: true, message: 'Please enter your work email.' }]}
                                >
                                    <Input
                                        autoComplete="email"
                                        prefix={<MailOutlined />}
                                        placeholder='you@company.com'
                                        onChange={(e) => {
                                            setErrorMessage("")
                                            setUsername(e.target.value)
                                        }}
                                    />
                                </Form.Item>
                                <Form.Item
                                    {...propsItem}
                                    label="Password"
                                    rules={[{ required: true, message: 'Please enter your password.' }]}
                                    name="password"
                                    style={styleFormItem}
                                >
                                    <Input.Password
                                        autoComplete="current-password"
                                        className='password'
                                        prefix={<LockOutlined />}
                                        placeholder='Enter your password'
                                        onChange={(e) => {
                                            setErrorMessage("")
                                            setPassword(e.target.value)
                                        }}
                                    />
                                </Form.Item>

                                {(Constant.useCaptchaValidationLogin && (tryLoginCount >= 3)) && <>
                                    <Form.Item {...propsItem} style={styleFormItem}>
                                        {captchaUI}
                                    </Form.Item>
                                    <Form.Item
                                        {...propsItem}
                                        label="Security code"
                                        name="captcha"
                                        style={styleFormItem}
                                        rules={[{
                                            validator: () => {
                                                if (!isValidCaptcha) {
                                                    generateCaptcha()
                                                    return Promise.reject(new Error('Invalid Captcha!'));
                                                }
                                                return Promise.resolve();
                                            }
                                        }]}
                                    >
                                        <Input placeholder='Enter security code' onChange={(e) => {
                                            setCaptcha(e.target.value)
                                            handleInputCaptchaChange(e.target.value)
                                        }} />
                                    </Form.Item>
                                </>}

                                <div className="login-form-links">
                                    <span />
                                    <Link to={RouteConstant.forgotPassword}>Forgot password?</Link>
                                </div>

                                <Button
                                    block
                                    className="login-submit"
                                    type='primary'
                                    htmlType='submit'
                                    loading={isLoadingLogin}
                                    size='large'
                                    disabled={isAlreadyHaveJWT}
                                >
                                    Sign in
                                    {!isLoadingLogin ? <ArrowRightOutlined /> : null}
                                </Button>

                            </Form>

                            <p className="login-register-link">
                                New to WorkPlansIQ?{' '}
                                <Link to="/register">Sign Up</Link>
                            </p>
                            <p className="login-security-note">
                                <SafetyCertificateOutlined /> Secure access to your WorkPlansIQ account
                            </p>
                        </Card>
                    </section>
                </div>
            </div>
        </div >
    )
}
